<template>
    <div class="warnings-table-page weeduc-admin-page corporate-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="$t('admin.warnings.title')"
                    :subtitle="$t('admin.warnings.subtitleManage')"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions">
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="lg"
                    class="text-none"
                    :title="$t('admin.warnings.createTitle')"
                    @click="openNewWarningDialog"
                >
                    <v-icon class="me-1" size="small">mdi-plus</v-icon>
                    {{ $t('admin.warnings.new') }}
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <section class="corporate-kpi-grid" :aria-label="$t('admin.courses.summary')">
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.total }}</strong>
                    <span class="corporate-kpi__label">{{ $t('admin.warnings.total') }}</span>
                </article>
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.active }}</strong>
                    <span class="corporate-kpi__label">{{ $t('admin.courses.statusActivePlural') }}</span>
                </article>
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.inactive }}</strong>
                    <span class="corporate-kpi__label">{{ $t('admin.courses.statusInactivePlural') }}</span>
                </article>
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.filtered }}</strong>
                    <span class="corporate-kpi__label">{{ $t('admin.warnings.found') }}</span>
                </article>
            </section>

            <div class="weeduc-admin-page__panel">
                <div class="weeduc-admin-page__toolbar corporate-access-toolbar">
                    <v-text-field
                        v-model="searchText"
                        :label="$t('common.search')"
                        :placeholder="$t('admin.warnings.searchPlaceholder')"
                        variant="outlined"
                        density="compact"
                        hide-details
                        clearable
                        class="weeduc-admin-v-field"
                        append-inner-icon="mdi-magnify"
                        @update:model-value="onSearch"
                    />
                    <v-select
                        v-model="statusFilter"
                        :items="statusFilterOptions"
                        item-title="text"
                        item-value="value"
                        :label="$t('common.status')"
                        variant="outlined"
                        density="compact"
                        hide-details
                        class="weeduc-admin-v-field"
                        :menu-props="adminSelectMenuProps"
                        @update:modelValue="filtrarAvisos"
                    />
                </div>

                <div class="weeduc-admin-page__table-section">
                    <div v-if="!hasReady" class="corporate-placeholder">
                        <Loader :isActive="false" />
                    </div>
                    <div v-else-if="!paginatedWarnings.length" class="corporate-placeholder">
                        {{ $t('admin.warnings.empty') }}
                    </div>
                    <div v-else class="corporate-access-list">
                        <article
                            v-for="warning in paginatedWarnings"
                            :key="warning.id"
                            class="corporate-access-card"
                        >
                            <div class="corporate-access-card__main">
                                <div
                                    class="corporate-access-card__icon"
                                    :class="warning.status === 1 ? 'is-active' : 'is-inactive'"
                                >
                                    <fa icon="fa-bullhorn" />
                                </div>
                                <div class="corporate-access-card__body">
                                    <div class="corporate-access-card__title-row">
                                        <h3>{{ warning.title || '—' }}</h3>
                                        <span
                                            class="corporate-access-chip"
                                            :class="warning.status === 1 ? 'is-active' : 'is-inactive'"
                                        >
                                            {{ getStatusText(warning.status) }}
                                        </span>
                                    </div>
                                    <div class="corporate-access-card__meta">
                                        <span>
                                            {{ truncateDescription(warning.description) || $t('admin.warnings.noDescription') }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="corporate-access-card__actions">
                                <v-btn
                                    size="small"
                                    variant="tonal"
                                    color="primary"
                                    rounded="lg"
                                    class="text-none"
                                    @click="openEditWarningDialog(warning)"
                                >
                                    Editar
                                </v-btn>
                                <v-btn
                                    size="small"
                                    variant="text"
                                    color="error"
                                    rounded="lg"
                                    class="text-none"
                                    @click="confirmDelete(warning)"
                                >
                                    Excluir
                                </v-btn>
                            </div>
                        </article>
                    </div>
                </div>

                <v-responsive
                    v-if="hasReady && filteredWarnings.length"
                    class="weeduc-admin-page__pagination"
                >
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        :total-visible="5"
                        @update:model-value="onPageChange"
                    />
                </v-responsive>
            </div>
        </div>

        <!-- Create New Warning Dialog -->
        <v-dialog v-model="newWarningDialog" max-width="600">
            <v-card class="modal-card">
                <v-card-title>
                    <h3 class="mb-0">Criar Novo Aviso</h3>
                </v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="newWarning.title"
                        :label="$t('admin.warnings.noticeTitleLabel')"
                        required
                        variant="outlined"
                        hide-details
                        density="compact"
                        class="mb-4"
                    />

                    <v-textarea
                        v-model="newWarning.description"
                        :label="$t('admin.warnings.noticeDescriptionLabel')"
                        required
                        auto-grow
                        rows="4"
                        variant="outlined"
                        hide-details
                        density="compact"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="flex-grow-1 cancel-btn"
                        color="grey-darken-4"
                        variant="tonal"
                        @click="closeNewWarningDialog"
                    >
                        Cancelar
                    </v-btn>
                    <v-btn
                        class="flex-grow-1 text-white"
                        color="primary"
                        variant="flat"
                        :loading="isCreatingWarning"
                        :disabled="
                            !newWarning.title.trim() || isCreatingWarning
                        "
                        @click="createNewWarning"
                    >
                        Publicar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Edit Warning Dialog -->
        <v-dialog v-model="editWarningDialog" max-width="600">
            <v-card class="modal-card">
                <v-card-title>
                    <h3 class="mb-0">Editar Aviso</h3>
                </v-card-title>
                <v-card-text>
                    <div class="mb-4">
                        <v-switch
                            v-model="editingWarning.status"
                            :label="getStatusText(editingWarning.status)"
                            :true-value="1"
                            :false-value="0"
                            color="primary"
                            hide-details
                        />
                    </div>

                    <v-text-field
                        v-model="editingWarning.title"
                        :label="$t('admin.warnings.noticeTitleLabel')"
                        required
                        variant="outlined"
                        hide-details
                        density="compact"
                        class="mb-4"
                    />

                    <v-textarea
                        v-model="editingWarning.description"
                        :label="$t('admin.warnings.noticeDescriptionLabel')"
                        required
                        auto-grow
                        rows="4"
                        variant="outlined"
                        hide-details
                        density="compact"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="flex-grow-1 cancel-btn"
                        color="grey-darken-4"
                        variant="tonal"
                        @click="closeEditWarningDialog"
                    >
                        Cancelar
                    </v-btn>
                    <v-btn
                        class="flex-grow-1 text-white"
                        color="primary"
                        variant="flat"
                        :loading="isUpdatingWarning"
                        :disabled="
                            !editingWarning.title.trim() || isUpdatingWarning
                        "
                        @click="updateWarning"
                    >
                        Salvar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" max-width="480">
            <v-card class="modal-card">
                <v-card-title>
                    <h3 class="mb-0">Excluir Aviso</h3>
                </v-card-title>
                <v-card-subtitle>
                    Esta ação não poderá ser desfeita.
                </v-card-subtitle>
                <v-card-text>
                    Tem certeza que deseja excluir o aviso
                    <strong>"{{ warningToDelete?.title }}"</strong>?
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="flex-grow-1 cancel-btn"
                        color="grey-darken-4"
                        variant="tonal"
                        @click="deleteDialog = false"
                    >
                        Cancelar
                    </v-btn>
                    <v-btn
                        class="flex-grow-1 text-white"
                        color="error"
                        variant="flat"
                        :loading="isDeletingWarning"
                        :disabled="isDeletingWarning"
                        @click="confirmDeleteWarning"
                    >
                        Excluir
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import Loader from '@/components/Loader/loader.vue';
    import TitlePage from '@/components/Weflix/titlePage.vue';
    import api from '@/utils/notification';
    import { debounce } from 'lodash';
    import { mapState } from 'vuex';
    import { toast } from 'vue3-toastify';

    export default {
        components: {
            Loader,
            TitlePage,
        },
        data() {
            return {
                notificationsData: [],
                searchText: '',
                currentPage: 1,
                itemsPerPage: 10,
                hasReady: false,
                isCreatingWarning: false,
                isUpdatingWarning: false,
                isDeletingWarning: false,
                statusFilter: 'todos',
                newWarningDialog: false,
                editWarningDialog: false,
                deleteDialog: false,
                warningToDelete: null,
                newWarning: {
                    title: '',
                    description: '',
                    status: 1,
                },
                editingWarning: {
                    id: null,
                    title: '',
                    description: '',
                    status: 1,
                },
            };
        },
        computed: {
            ...mapState(['token', 'numberIntensTable', 'dark']),

            adminSelectMenuProps() {
                return this.dark
                    ? { contentClass: 'weeduc-admin-select-menu-dark' }
                    : {};
            },

            statusFilterOptions() {
                return [
                    { text: this.$t('common.all'), value: 'todos' },
                    { text: this.$t('common.active'), value: 1 },
                    { text: this.$t('common.inactive'), value: 0 },
                ];
            },

            stats() {
                const total = this.notificationsData.length;
                const active = this.notificationsData.filter(
                    (warning) => warning.status === 1
                ).length;
                return {
                    total,
                    active,
                    inactive: total - active,
                    filtered: this.filteredWarnings.length,
                };
            },

            paginatedWarnings() {
                const startIndex = (this.currentPage - 1) * this.itemsPerPage;
                const endIndex = startIndex + this.itemsPerPage;
                return this.filteredWarnings.slice(startIndex, endIndex);
            },

            totalPages() {
                return Math.ceil(
                    this.filteredWarnings.length / this.itemsPerPage
                );
            },

            filteredWarnings() {
                let filtered = this.notificationsData;

                if (this.searchText && this.searchText.trim() !== '') {
                    filtered = filtered.filter(
                        (warning) =>
                            warning.title
                                .toLowerCase()
                                .includes(this.searchText.toLowerCase()) ||
                            warning.description
                                .toLowerCase()
                                .includes(this.searchText.toLowerCase())
                    );
                }

                if (this.statusFilter !== 'todos') {
                    filtered = filtered.filter(
                        (warning) =>
                            String(warning.status) === String(this.statusFilter)
                    );
                }

                return filtered;
            },
        },
        created() {
            this.getDataNotification();
        },
        methods: {
            getStatusText(status) {
                return status === 1
                    ? this.$t('common.active')
                    : this.$t('common.inactive');
            },

            truncateDescription(description, maxLength = 100) {
                if (!description) return '';
                return description.length > maxLength
                    ? description.substring(0, maxLength) + '...'
                    : description;
            },

            openNewWarningDialog() {
                this.newWarningDialog = true;
            },

            closeNewWarningDialog() {
                this.newWarningDialog = false;
                this.newWarning = {
                    title: '',
                    description: '',
                    status: 1,
                };
            },

            openEditWarningDialog(warning) {
                this.editingWarning = {
                    id: warning.id,
                    title: warning.title,
                    description: warning.description,
                    status: warning.status,
                };
                this.editWarningDialog = true;
            },

            closeEditWarningDialog() {
                this.editWarningDialog = false;
                this.editingWarning = {
                    id: null,
                    title: '',
                    description: '',
                    status: 1,
                };
            },

            confirmDelete(warning) {
                this.warningToDelete = warning;
                this.deleteDialog = true;
            },

            async confirmDeleteWarning() {
                if (this.warningToDelete) {
                    this.isDeletingWarning = true;
                    try {
                        await this.deleteWarning(this.warningToDelete.id);
                        this.deleteDialog = false;
                        this.warningToDelete = null;
                    } catch (error) {
                        console.error('Error deleting warning:', error);
                    } finally {
                        this.isDeletingWarning = false;
                    }
                }
            },

            async createNewWarning() {
                if (!this.newWarning.title.trim()) {
                    toast.error(this.$t('admin.warnings.titleRequired'));
                    return;
                }

                this.isCreatingWarning = true;
                const payload = {
                    title: this.newWarning.title.trim(),
                    description: this.newWarning.description.trim(),
                    status: 1,
                };

                try {
                    await api.createNewNotification(payload, this.token);
                    await this.getDataNotification();
                    toast.success(this.$t('admin.warnings.createSuccess'));
                    this.closeNewWarningDialog();
                } catch (error) {
                    toast.error(this.$t('admin.warnings.createError'));
                    console.error('Erro ao criar aviso:', error);
                } finally {
                    this.isCreatingWarning = false;
                }
            },

            async updateWarning() {
                if (!this.editingWarning.title.trim()) {
                    toast.error(this.$t('admin.warnings.titleRequired'));
                    return;
                }

                this.isUpdatingWarning = true;
                const payload = {
                    id: this.editingWarning.id,
                    title: this.editingWarning.title.trim(),
                    description: this.editingWarning.description.trim(),
                    status: this.editingWarning.status,
                };

                try {
                    await api.editNotification(
                        this.editingWarning.id,
                        this.token,
                        payload
                    );
                    await this.getDataNotification();
                    toast.success(this.$t('admin.warnings.updateSuccess'));
                    this.closeEditWarningDialog();
                } catch (error) {
                    toast.error(this.$t('admin.warnings.updateError'));
                    console.error('Erro ao atualizar aviso:', error);
                } finally {
                    this.isUpdatingWarning = false;
                }
            },

            async deleteWarning(id) {
                try {
                    const payload = {
                        id: id,
                        status: 0,
                    };
                    await api.editNotification(id, this.token, payload);
                    await this.getDataNotification();
                    toast.success(this.$t('admin.warnings.deleteSuccess'));
                } catch (error) {
                    toast.error(this.$t('admin.warnings.deleteError'));
                    console.error('Erro ao deletar aviso:', error);
                    throw error;
                }
            },

            async getDataNotification() {
                try {
                    this.hasReady = false;
                    const response = await api.getDataNotification(this.token);
                    this.notificationsData = response;
                } catch (error) {
                    toast.error(this.$t('admin.warnings.loadError'));
                    console.error('Erro ao carregar notificações', error);
                } finally {
                    this.hasReady = true;
                }
            },

            filtrarAvisos() {
                this.currentPage = 1;
            },

            onSearch: debounce(function () {
                this.currentPage = 1;
            }, 300),

            onPageChange() {},
        },
    };
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.corporate-access-toolbar {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
}

@media (min-width: 960px) {
    .corporate-access-toolbar {
        grid-template-columns: minmax(180px, 1.4fr) minmax(140px, 1fr);
        align-items: end;
    }
}

.corporate-access-list {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}

.corporate-access-card {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.1rem;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    background: #fff;
}

.corporate-access-card__main {
    display: flex;
    gap: 0.9rem;
    min-width: 0;
    flex: 1;
}

.corporate-access-card__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: #eef1fb;
    color: #3b3f8c;
}

.corporate-access-card__icon.is-active {
    background: #e8f5f0;
    color: #1f7a57;
}

.corporate-access-card__icon.is-inactive {
    background: #f0f2f5;
    color: #5c6370;
}

.corporate-access-card__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
    margin-bottom: 0.4rem;
}

.corporate-access-card__title-row h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #1a1d26;
}

.corporate-access-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.15rem 0.55rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 700;
}

.corporate-access-chip.is-active {
    background: #e8f5f0;
    color: #1f7a57;
}

.corporate-access-chip.is-inactive {
    background: #f0f2f5;
    color: #5c6370;
}

.corporate-access-card__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem 1.1rem;
    font-size: 0.82rem;
    color: #5c6370;
}

.corporate-access-card__meta span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
}

.corporate-access-card__actions {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}

.dashboard.dark .corporate-access-card,
.dark .corporate-access-card {
    background: #252730;
    border-color: #353945;
}

.dashboard.dark .corporate-access-card__title-row h3,
.dark .corporate-access-card__title-row h3 {
    color: #f1f3f7;
}

.dashboard.dark .corporate-access-card__icon,
.dark .corporate-access-card__icon {
    background: rgba(165, 180, 252, 0.15);
    color: #c4b5fd;
}

.dashboard.dark .corporate-access-card__icon.is-active,
.dark .corporate-access-card__icon.is-active {
    background: rgba(31, 122, 87, 0.2);
    color: #6ee7b7;
}

.dashboard.dark .corporate-access-card__icon.is-inactive,
.dark .corporate-access-card__icon.is-inactive {
    background: rgba(156, 163, 180, 0.15);
    color: #9ca3b4;
}

.dashboard.dark .corporate-access-chip.is-active,
.dark .corporate-access-chip.is-active {
    background: rgba(31, 122, 87, 0.2);
    color: #6ee7b7;
}

.dashboard.dark .corporate-access-chip.is-inactive,
.dark .corporate-access-chip.is-inactive {
    background: rgba(156, 163, 180, 0.15);
    color: #9ca3b4;
}

.dashboard.dark .corporate-access-card__meta,
.dark .corporate-access-card__meta {
    color: #9ca3b4;
}

.dashboard.dark .corporate-access-card__actions :deep(.v-btn--variant-text),
.dark .corporate-access-card__actions :deep(.v-btn--variant-text) {
    color: #c4b5fd !important;
}
</style>
